if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        if (this.hasAttribute('data-quadri-options')) {
          this.connectQuadriPicker();
          return;
        }
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      connectQuadriPicker() {
        if (this.quadriConnected) return;
        this.quadriConnected = true;
        this.quadriSequence = 0;
        this.quadriGroups = Array.from(this.querySelectorAll('[data-quadri-option-index]')).map((element) => {
          const select = element.querySelector('select');
          return {
            element,
            index: Number(element.dataset.quadriOptionIndex),
            name: element.dataset.quadriOptionName.trim().toLowerCase(),
            select,
            values: Array.from(select ? select.options : element.querySelectorAll('input[type="radio"]')),
          };
        });
        this.quadriReady = this.loadQuadriVariants();
        this.addEventListener('change', async (event) => {
          if (!event.target.matches('select, input[type="radio"]')) return;
          const sequence = ++this.quadriSequence;
          const productInfo = this.closest('product-info');
          productInfo.abortController?.abort();
          productInfo.toggleAddButton(true);
          try {
            if (!this.quadriReady) this.quadriReady = this.loadQuadriVariants();
            await this.quadriReady;
            if (sequence !== this.quadriSequence || !this.isCurrentQuadriPicker()) return;
            const changedGroup = this.quadriGroups.find((group) => group.element.contains(event.target));
            this.normalizeQuadriOptions(changedGroup);
            this.publishQuadriSelection(event);
          } catch (error) {
            this.showQuadriMessage('Impossibile caricare le opzioni. Seleziona nuovamente un’opzione per riprovare.');
            this.quadriReady = null;
            console.error(error);
          }
        });
        // A picker is inserted again by Hyper on every section response.
        // Defer until its children and the surrounding product-info are present.
        this.quadriReady.then(() => {
          if (this.quadriSequence || !this.isCurrentQuadriPicker()) return;
          if (this.normalizeQuadriOptions()) this.publishQuadriSelection({ target: this });
        }).catch((error) => {
          this.showQuadriMessage('Impossibile caricare le opzioni. Seleziona nuovamente un’opzione per riprovare.');
          this.quadriReady = null;
          console.error(error);
        });
      }

      isCurrentQuadriPicker() {
        return this.isConnected && this.closest('product-info')?.variantSelectors === this;
      }

      async loadQuadriVariants() {
        this.setAttribute('aria-busy', 'true');
        try {
          const data = JSON.parse(this.querySelector('[data-quadri-variant-data]').textContent);
          let combinations = data.options;
          let available = data.available;
          // Shopify's unpaginated product.variants stops at 250. Load the remainder
          // from a small, paginated section rather than silently dropping variants.
          if (data.total > combinations.length) {
            const productInfo = this.closest('product-info');
            const key = this.dataset.url + ':' + data.total;
            if (productInfo.quadriDataKey !== key || !productInfo.quadriDataPromise || Date.now() - productInfo.quadriDataTime > 30000) {
              productInfo.quadriDataKey = key;
              productInfo.quadriDataTime = Date.now();
              productInfo.quadriDataPromise = this.fetchQuadriVariantPages(data.total).catch((error) => {
                productInfo.quadriDataPromise = null;
                throw error;
              });
            }
            const complete = await productInfo.quadriDataPromise;
            combinations = complete.options;
            available = complete.available;
            // Refresh the inventory values included in the latest section response.
            for (let i = 0; i < data.options.length; i++) available[i] = data.available[i];
            const current = JSON.parse(this.querySelector('[data-selected-variant]')?.textContent || 'null');
            if (current) {
              const index = combinations.findIndex((values) => values.every((value, position) => value === current.options[position]));
              if (index !== -1) available[index] = current.available;
            }
          }
          if (combinations.length !== data.total || available.length !== combinations.length) {
            throw new Error('Incomplete product variant data');
          }
          this.quadriCombinations = combinations.filter((_, index) => available[index]);
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      async fetchQuadriVariantPages(total) {
        const pages = new Array(Math.ceil(total / 50));
        let nextPage = 0;
        const worker = async () => {
          while (nextPage < pages.length) {
            const index = nextPage++;
            const url = new URL(this.dataset.url, window.location.origin);
            url.search = '';
            url.searchParams.set('section_id', 'quadri-variant-data');
            url.searchParams.set('page', String(index + 1));
            const response = await fetch(url.toString());
            if (!response.ok) throw new Error('Variant data request failed');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const data = JSON.parse(html.querySelector('[data-quadri-variant-data]').textContent);
            if (data.page !== index + 1 || data.total !== total) throw new Error('Invalid variant page');
            pages[index] = data;
          }
        };
        await Promise.all(Array.from({ length: Math.min(4, pages.length) }, worker));
        return { options: pages.flatMap((page) => page.options), available: pages.flatMap((page) => page.available) };
      }

      normalizeQuadriOptions(changedGroup) {
        const selected = (group) => group.select?.value ?? group.values.find((value) => value.checked)?.value;
        const before = this.quadriGroups.map(selected);
        const material = this.quadriGroups.find((group) => group.name === 'materiale');
        const ordered = material ? [material, ...this.quadriGroups.filter((group) => group !== material)] : this.quadriGroups;
        const selectedValues = [];
        let candidates = this.quadriCombinations;
        let resetFollowing = false;

        for (const group of ordered) {
          const allowed = new Set(candidates.map((variant) => variant[group.index]));
          const visible = group.values.filter((value) => allowed.has(value.value));
          const current = selected(group);
          const choice = (!resetFollowing && visible.find((value) => value.value === current)) || visible[0];
          selectedValues[group.index] = choice?.value;

          for (const value of group.values) {
            const show = allowed.has(value.value);
            value.disabled = !show;
            value.hidden = !show;
            value.toggleAttribute('data-quadri-hidden', !show);
            // Availability now follows the normalized prefix, not the old selection.
            if (show) value.classList.remove('disabled');
            if (group.select) {
              value.selected = value === choice;
              if (value === choice) value.setAttribute('selected', 'selected');
              else value.removeAttribute('selected');
              // Detach incompatible options: mobile native selects can ignore hidden.
              if (show) group.select.appendChild(value);
              else value.remove();
              if (show) value.textContent = value.value;
            } else {
              value.checked = value === choice;
              value.toggleAttribute('checked', value === choice);
              const label = Array.from(group.element.querySelectorAll('label')).find((item) => item.htmlFor === value.id);
              if (label) {
                label.hidden = !show;
                label.toggleAttribute('data-quadri-hidden', !show);
                label.querySelector('.label-unavailable')?.setAttribute('hidden', '');
              }
            }
          }
          if (group.select) {
            group.select.value = choice?.value ?? '';
            group.select.disabled = !choice;
            if (choice) this.updateSelectedSwatchValue({ target: group.select });
          }
          const caption = group.element.querySelector('[data-selected-swatch-value]');
          if (caption) caption.textContent = choice?.value ?? '';
          group.element.hidden = !choice;
          group.element.toggleAttribute('data-quadri-hidden', !choice);
          candidates = choice ? candidates.filter((variant) => variant[group.index] === choice.value) : [];
          if (group === changedGroup) resetFollowing = true;
        }

        const productInfo = this.closest('product-info');
        productInfo.querySelectorAll('[data-quadri-product-title]').forEach((title) => {
          title.textContent = [title.dataset.quadriBaseTitle, ...selectedValues.filter(Boolean)].join(' - ');
        });
        productInfo.querySelectorAll('media-gallery[data-quadri-media]').forEach((gallery) => {
          const selectedMaterial = material ? selectedValues[material.index] || '' : '';
          // Keep the value even if media-gallery.js is still loading.
          gallery.dataset.quadriSelectedMaterial = selectedMaterial;
          gallery.filterQuadriMaterial?.(selectedMaterial);
        });
        this.showQuadriMessage(candidates.length ? '' : 'Nessuna combinazione disponibile al momento.');
        if (!candidates.length) {
          productInfo.setUnavailable();
          productInfo.updateVariantInputs('');
          productInfo.currentVariant = null;
        }
        return before.some((value, index) => value !== selectedValues[index]);
      }

      publishQuadriSelection(event) {
        const values = this.selectedOptionValues;
        if (values.length !== this.quadriGroups.length) return;
        const eventTarget = event.target.matches?.('select, input[type="radio"]') ? this.getInputForEventTarget(event.target) : null;
        const target = eventTarget || this.querySelector('select option:checked, input:checked');
        if (!target) return;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: { event, target, selectedOptionValues: values },
        });
      }

      showQuadriMessage(message) {
        const element = this.querySelector('[data-quadri-picker-message]');
        if (!element) return;
        element.textContent = message;
        element.hidden = !message;
      }


      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
